import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Check } from "lucide-react";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "~/atoms/accordion";
import { Badge } from "~/atoms/badge";
import { Button } from "~/atoms/button";
import { Checkbox } from "~/atoms/checkbox";
import { ToggleGroup, ToggleGroupItem } from "~/atoms/toggle-group";
import { richTextToPlainText } from "~/helpers/richText";
import { BulkSelectionBar } from "~/molecules/BulkSelectionBar";
import { RichTextView } from "~/molecules/RichTextView";
import { Toolbar } from "~/molecules/Toolbar";
import { useBulkSelection } from "~/hooks/useBulkSelection";
import { cn } from "~/lib/utils";

interface DescriptionConflict {
  id: number;
  title: string;
  currentDescription: string | null;
}

interface Props {
  templateDescription: string | null;
  conflicts: DescriptionConflict[];
  keepDescriptionIds: Set<number>;
  onKeepDescriptionIdsChange: (ids: Set<number>) => void;
}

type Tone = "keep" | "replace";

interface PanelProps {
  label: string;
  text: string | null;
  selected: boolean;
  tone: Tone;
}

function DescriptionPanel({ label, text, selected, tone }: PanelProps) {
  const { t } = useTranslation();

  return (
    <div
      className={cn(
        "flex min-w-0 flex-col gap-2 rounded-md border p-3 transition-colors",
        !selected && "border-dashed opacity-60",
        selected && tone === "keep" && "border-primary bg-primary/10",
        selected && tone === "replace" && "border-warning bg-warning/20",
      )}
    >
      <div className="flex items-center gap-2">
        <span className="text-xs font-medium">{label}</span>
        {selected && (
          <Badge
            variant={tone === "keep" ? "default" : "warn"}
            className="gap-1"
          >
            <Check className="size-3" />
            {t(($) => $.workflowTemplates.applyDialog.descriptions.willBeUsed)}
          </Badge>
        )}
      </div>
      {text ? (
        <RichTextView
          value={text}
          className="max-h-40 overflow-y-auto text-sm break-words"
        />
      ) : (
        <p className="text-muted-foreground text-sm italic">
          {t(($) => $.workflowTemplates.applyDialog.descriptions.noDescription)}
        </p>
      )}
    </div>
  );
}

interface RowProps {
  row: DescriptionConflict;
  templateDescription: string | null;
  templatePreview: string;
  keepCurrent: boolean;
  isSelected: boolean;
  onToggleSelection: () => void;
  onChoose: (keepCurrent: boolean) => void;
}

function DescriptionRow({
  row,
  templateDescription,
  templatePreview,
  keepCurrent,
  isSelected,
  onToggleSelection,
  onChoose,
}: RowProps) {
  const { t } = useTranslation();

  const preview = keepCurrent
    ? richTextToPlainText(row.currentDescription ?? "")
    : templatePreview;

  return (
    <AccordionItem
      value={String(row.id)}
      className={cn("px-3 transition-colors", !keepCurrent && "bg-warning/10")}
    >
      <div className="flex items-center gap-3">
        <Checkbox
          checked={isSelected}
          onCheckedChange={onToggleSelection}
          aria-label={t(($) => $.commons.buttons.selectItem, {
            name: row.title,
          })}
        />
        <div className="min-w-0 flex-1">
          <AccordionTrigger className="min-w-0 cursor-pointer items-center gap-3 py-3 hover:no-underline">
            <div className="min-w-0 flex-1 text-left">
              <div className="truncate">{row.title}</div>
              <div className="text-muted-foreground truncate text-xs font-normal">
                {preview ||
                  t(
                    ($) =>
                      $.workflowTemplates.applyDialog.descriptions
                        .noDescription,
                  )}
              </div>
            </div>
          </AccordionTrigger>
        </div>
        <ToggleGroup
          type="single"
          variant="outline"
          size="sm"
          spacing={0}
          value={keepCurrent ? "current" : "template"}
          onValueChange={(value) => {
            if (value) onChoose(value === "current");
          }}
        >
          <ToggleGroupItem
            value="current"
            className="data-[state=on]:bg-primary data-[state=on]:text-primary-foreground data-[state=on]:hover:bg-primary"
          >
            {t(($) => $.workflowTemplates.applyDialog.descriptions.keepCurrent)}
          </ToggleGroupItem>
          <ToggleGroupItem
            value="template"
            className="data-[state=on]:bg-warning data-[state=on]:text-warning-foreground data-[state=on]:hover:bg-warning"
          >
            {t(($) => $.workflowTemplates.applyDialog.descriptions.useTemplate)}
          </ToggleGroupItem>
        </ToggleGroup>
      </div>
      {/* h-auto overrides the atom's fixed --radix-accordion-content-height: it is
          measured once on open, and these panels change height when the choice
          toggles, which would otherwise clip them. */}
      <AccordionContent className="h-auto pb-4">
        <div className="grid grid-cols-1 gap-3 lg:grid-cols-2">
          <DescriptionPanel
            label={t(
              ($) =>
                $.workflowTemplates.applyDialog.descriptions.currentDescription,
            )}
            text={row.currentDescription}
            selected={keepCurrent}
            tone="keep"
          />
          <DescriptionPanel
            label={t(
              ($) =>
                $.workflowTemplates.applyDialog.descriptions
                  .templateDescription,
            )}
            text={templateDescription}
            selected={!keepCurrent}
            tone="replace"
          />
        </div>
      </AccordionContent>
    </AccordionItem>
  );
}

export function ApplyTemplateDescriptionsStep({
  templateDescription,
  conflicts,
  keepDescriptionIds,
  onKeepDescriptionIdsChange,
}: Props) {
  const { t } = useTranslation();

  const [expandedIds, setExpandedIds] = useState<string[]>([]);
  const { selectedIds, allSelected, toggleRow, toggleAll, clearSelection } =
    useBulkSelection(conflicts.map((row) => row.id));

  const allExpanded = expandedIds.length === conflicts.length;
  const toggleExpandAll = () =>
    setExpandedIds(allExpanded ? [] : conflicts.map((row) => String(row.id)));

  const applyChoice = (rowIds: Iterable<number>, keepCurrent: boolean) => {
    const next = new Set(keepDescriptionIds);
    for (const id of rowIds) {
      if (keepCurrent) {
        next.add(id);
      } else {
        next.delete(id);
      }
    }
    onKeepDescriptionIdsChange(next);
  };

  // Every "Use template" row shows the same preview, so parse it once here.
  const templatePreview = richTextToPlainText(templateDescription ?? "");

  return (
    <div className="w-[62rem] max-w-[calc(90vw-2rem)] min-w-0 rounded-md border">
      <div className="bg-muted rounded-t-md border-b px-3 py-2">
        <Toolbar
          left={
            <>
              <Checkbox
                checked={allSelected}
                onCheckedChange={toggleAll}
                aria-label={t(($) => $.commons.buttons.selectAll)}
              />
              <BulkSelectionBar
                count={selectedIds.size}
                onUnselectAll={clearSelection}
              />
              <Button
                size="xs"
                variant="outline"
                disabled={selectedIds.size === 0}
                onClick={() => applyChoice(selectedIds, true)}
              >
                {t(
                  ($) =>
                    $.workflowTemplates.applyDialog.descriptions.keepCurrent,
                )}
              </Button>
              <Button
                size="xs"
                variant="outline"
                disabled={selectedIds.size === 0}
                onClick={() => applyChoice(selectedIds, false)}
              >
                {t(
                  ($) =>
                    $.workflowTemplates.applyDialog.descriptions.useTemplate,
                )}
              </Button>
            </>
          }
          right={
            <Button size="xs" variant="ghost" onClick={toggleExpandAll}>
              {allExpanded
                ? t(($) => $.commons.buttons.collapseAll)
                : t(($) => $.commons.buttons.expandAll)}
            </Button>
          }
        />
      </div>

      <Accordion
        type="multiple"
        value={expandedIds}
        onValueChange={setExpandedIds}
        className="max-h-[50vh] overflow-y-auto"
      >
        {conflicts.map((row) => (
          <DescriptionRow
            key={row.id}
            row={row}
            templateDescription={templateDescription}
            templatePreview={templatePreview}
            keepCurrent={keepDescriptionIds.has(row.id)}
            isSelected={selectedIds.has(row.id)}
            onToggleSelection={() => toggleRow(row.id)}
            onChoose={(keepCurrent) => applyChoice([row.id], keepCurrent)}
          />
        ))}
      </Accordion>
    </div>
  );
}
